/**
 * lib/paymentBatches.ts
 * ------------------------------------------------------------------
 * PAYING MANY SUPPLIERS AT ONCE, WITHOUT PAYING ANY OF THEM TWICE.
 *
 * A voucher approved on its own is paid by somebody typing it into the
 * bank. Ninety of them are paid by a file. The file is faster and it is
 * also where a school pays the same invoice twice, because the second
 * payment looks exactly like the first and nothing connects them.
 *
 * BATCH MEMBERSHIP IS DERIVED, NOT STAMPED ON THE VOUCHER. A voucher is
 * "already in a run" if some live batch lists it — computed here, the
 * same rule the ledger and the three-way match follow. The alternative
 * is an `in_batch` status on the voucher, which is a second place the
 * truth lives and therefore a place it can be wrong: a batch cancelled
 * without its vouchers being released strands them unpayable, and
 * nothing in the system can tell that from a voucher legitimately mid-
 * flight.
 *
 * PAID MEANS THE BANK CONFIRMED IT, NOT THAT WE SENT A FILE. A bulk file
 * can be rejected wholesale — one bad character does it — and the
 * rejection arrives the next day. Marking vouchers paid on export means
 * the books say the money left on a day it did not, and the correction
 * has to be found before anyone notices. So export and confirmation are
 * two separate acts, and only the second one pays anything.
 *
 * THE DUPLICATE GUARD IS A WARNING, NOT A BLOCK. A school does
 * legitimately pay one supplier the same amount twice in a week — two
 * identical deliveries, a standing weekly charge. Blocking it would
 * teach people to work around the block. Naming it makes somebody look.
 *
 * PURE. Vouchers and batches in, an answer out.
 * ------------------------------------------------------------------ */
import { round2 } from '@/lib/financeCore';
import type { Voucher } from '@/lib/vouchers';
import type { SupplierBank } from '@/lib/suppliers';
import type { BankFileRow } from '@/lib/bankFile';

export const BATCH_COLLECTION = 'financePaymentBatches';
export const batchCounterId = (year: string): string => `paymentBatches-${year}`;
export const formatBatchNo = (seq: number, year: string): string =>
  `PB/${year}/${String(seq).padStart(4, '0')}`;

/**
 * `exported` is the meaningful state: before it the run is the school's
 * to change, after it a file is with the bank and the school no longer
 * controls the outcome. That is why a batch can only be cancelled while
 * it is still a draft.
 */
export type BatchStatus = 'draft' | 'exported' | 'confirmed' | 'cancelled';

export const BATCH_STATUS_LABEL: Record<BatchStatus, string> = {
  draft: 'Draft — being assembled',
  exported: 'File sent to the bank',
  confirmed: 'Confirmed paid',
  cancelled: 'Cancelled',
};

export interface PaymentBatch {
  id: string;
  batchNo: string;
  fy: string;
  status: BatchStatus;
  /** The vouchers in this run. The only record of membership. */
  voucherIds: string[];
  /** The day the money is meant to leave, which is not the day it was assembled. */
  valueDate: string;
  bankFormatId: string;
  total: number;
  count: number;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  exportedAt?: string;
  exportedBy?: string;
  exportedByName?: string;
  /** What the bank said, and when. */
  confirmedAt?: string;
  confirmedBy?: string;
  confirmedByName?: string;
  bankRef?: string;
  cancelledAt?: string;
  cancelledReason?: string;
  updatedAt?: string;
}

/** A batch still holding its vouchers. A cancelled one releases them. */
export const BATCH_LIVE: BatchStatus[] = ['draft', 'exported', 'confirmed'];

/**
 * Which vouchers are already committed to a run.
 *
 * The set, not a boolean per voucher, because every caller needs to ask
 * it repeatedly while rendering a list of hundreds.
 */
export function vouchersInBatches(batches: PaymentBatch[]): Set<string> {
  const out = new Set<string>();
  for (const b of batches) {
    if (!BATCH_LIVE.includes(b.status)) continue;
    for (const id of b.voucherIds ?? []) out.add(id);
  }
  return out;
}

/**
 * What may be added to a run right now.
 *
 * APPROVED ONLY. Showing everything owed is a question about visibility
 * and belongs on the screen; what may actually be PAID is this, and the
 * approval gate does not move because a payment is being made in bulk.
 */
export function batchableVouchers(
  vouchers: Voucher[],
  batches: PaymentBatch[],
): Voucher[] {
  const taken = vouchersInBatches(batches);
  return vouchers
    .filter((v) => v.currentStatus === 'approved')
    .filter((v) => !taken.has(v.id))
    .sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''));
}

export function batchTotal(vouchers: Voucher[]): number {
  return round2(vouchers.reduce((a, v) => a + (Number(v.total) || 0), 0));
}

/* ============================== the file ============================== */

/**
 * Turn the run into rows the bank file writer understands.
 *
 * THE BANKING COMES FROM THE VOUCHER'S OWN SNAPSHOT WHERE THERE IS ONE.
 * `payeeAtApproval` is what the approver authorised; the register may
 * have changed since. Paying the register instead would route money to
 * an account nobody approved, which is the whole reason the snapshot
 * exists — and doing it in bulk, to ninety payees, is the version of
 * that mistake nobody catches by eye.
 */
export function batchToBankRows(
  vouchers: Voucher[],
  supplierBank: (supplierId: string) => SupplierBank | undefined,
): BankFileRow[] {
  return vouchers.map((v) => {
    const snapshot = v.payeeAtApproval?.bank;
    const registry = v.supplierId ? supplierBank(v.supplierId) : undefined;
    const bank = snapshot ?? registry ?? {};
    return {
      payeeName: v.payeeName,
      bank: {
        accountName: bank.accountName,
        accountNumber: bank.accountNumber,
        bankName: bank.bankName,
        branch: bank.branch,
      },
      amount: Number(v.total) || 0,
      reference: v.paymentRef || v.voucherNo,
    };
  });
}

/* =========================== the duplicate guard =========================== */

export interface DuplicateWarning {
  voucherId: string;
  voucherNo: string;
  payeeName: string;
  amount: number;
  /** The earlier payment this one resembles. */
  againstVoucherNo: string;
  againstDate: string;
  daysApart: number;
  /**
   * How sure we are. An invoice number repeated is a duplicate; an amount
   * repeated is a question. Both are shown, because suppressing the weaker
   * one would lose the duplicates raised without an invoice number — but a
   * reader must be able to tell which is which, or the certain ones drown.
   */
  confidence: 'certain' | 'possible';
  /** The invoice number, when that is what matched. */
  supplierInvoiceNo?: string;
}

/**
 * Two payments to one payee for the same amount, close together.
 *
 * Far more often a duplicate than a coincidence — a supplier chasing an
 * invoice already paid, and somebody helpfully raising it again. It is
 * checked against vouchers ALREADY PAID as well as the rest of this run,
 * because the expensive version is paying today what was paid last week.
 *
 * A WARNING, DELIBERATELY. Identical weekly charges are real. What this
 * has to do is make somebody look, not make them find a way round.
 */
export function duplicateWarnings(
  candidate: Voucher[],
  history: Voucher[],
  withinDays = 7,
  asOf: Date = new Date(),
): DuplicateWarning[] {
  const out: DuplicateWarning[] = [];
  const today = asOf.getTime();

  const payee = (v: Voucher) => (v.payeeName || '').trim().toLowerCase();

  /** The amount match: same payee, same money. A question, not a verdict. */
  const amountKey = (v: Voucher) => `${payee(v)}|${round2(Number(v.total) || 0)}`;

  /**
   * The invoice match: same payee, same invoice number. A verdict.
   *
   * Note it does NOT include the amount. A supplier who re-issues an
   * invoice with a correction still has one invoice, and matching on the
   * amount as well would let the corrected copy through as a new bill —
   * which is the duplicate this is for.
   */
  const invoiceKey = (v: Voucher) => {
    const ref = (v.supplierInvoiceNo || '').trim().toLowerCase();
    return ref ? `${payee(v)}|${ref}` : null;
  };

  const priorPaid = history.filter((v) => v.currentStatus === 'paid');

  const compare = (v: Voucher, other: Voucher): DuplicateWarning['confidence'] | null => {
    const a = invoiceKey(v);
    const b = invoiceKey(other);
    if (a && b) return a === b ? 'certain' : null;
    return amountKey(v) === amountKey(other) ? 'possible' : null;
  };

  candidate.forEach((v, i) => {
    for (const p of priorPaid) {
      const confidence = compare(v, p);
      if (!confidence) continue;
      const when = p.paidAt ? Date.parse(p.paidAt) : NaN;
      if (Number.isNaN(when)) continue;
      const days = Math.round((today - when) / 86400000);
      /* An invoice number paid twice is a duplicate whenever it happened.
         The window only ever made sense for the weaker amount match. */
      if (confidence === 'possible' && (days < 0 || days > withinDays)) continue;
      if (days < 0) continue;
      out.push({
        voucherId: v.id, voucherNo: v.voucherNo, payeeName: v.payeeName,
        amount: Number(v.total) || 0,
        againstVoucherNo: p.voucherNo,
        againstDate: (p.paidAt ?? '').slice(0, 10),
        daysApart: days,
        confidence,
        ...(v.supplierInvoiceNo ? { supplierInvoiceNo: v.supplierInvoiceNo } : {}),
      });
    }

    /* And against the rest of this same run — the easiest duplicate of
       all to create, because both rows are added in one sitting. */
    for (let j = 0; j < i; j += 1) {
      const confidence = compare(v, candidate[j]);
      if (!confidence) continue;
      out.push({
        voucherId: v.id, voucherNo: v.voucherNo, payeeName: v.payeeName,
        amount: Number(v.total) || 0,
        againstVoucherNo: candidate[j].voucherNo,
        againstDate: 'this run',
        daysApart: 0,
        confidence,
        ...(v.supplierInvoiceNo ? { supplierInvoiceNo: v.supplierInvoiceNo } : {}),
      });
    }
  });

  return out;
}

/* ============================== transitions ============================== */

export type BatchAction = 'export' | 'confirm' | 'cancel';

/** Why this cannot be done, or null. A sentence, so nobody re-clicks. */
export function batchActionProblem(
  batch: PaymentBatch,
  action: BatchAction,
): string | null {
  switch (action) {
    case 'export':
      if (batch.status !== 'draft') {
        return `This run is "${BATCH_STATUS_LABEL[batch.status]}" — only a draft can be sent to the bank.`;
      }
      if ((batch.voucherIds ?? []).length === 0) {
        return 'There is nothing in this run.';
      }
      return null;

    case 'confirm':
      if (batch.status !== 'exported') {
        return batch.status === 'confirmed'
          ? 'This run has already been confirmed.'
          : 'A run is confirmed after its file has gone to the bank, not before.';
      }
      return null;

    /* Once the file is with the bank the school no longer controls the
       outcome, so "cancelled" would be a claim it cannot make. A run that
       the bank rejects is corrected by raising the next one. */
    case 'cancel':
      if (batch.status !== 'draft') {
        return 'Only a draft run can be cancelled — the file for this one is already with the bank.';
      }
      return null;

    default:
      return 'That is not something that can be done to a payment run.';
  }
}
